import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import { Incomes } from 'src/@types/Choqorak/MainTables/Income/Income';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import useThrottle from 'src/hooks/useThrottle';

// ----------------------------------------------------------------------

export default observer(function IncomeNewEditForm() {
  const { IncomeStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { createIncome, updateIncome, editMode, selectedIncome, clearSelectedIncome } = IncomeStore;
  const { loadCurrencyTypeDDL, CurrencyTypeOption, loadIncomeCategoryDDL, IncomeCategoryOption } =
    ChoqorakCommonDropdown;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewIncomeSchema = Yup.object().shape({
    incomeCategoryId: Yup.string().required(`${translate('Validation.incomeCategoryId')}`),
    description: Yup.string().required(`${translate('Validation.description')}`),
    carrencyId: Yup.string().required(`${translate('Validation.carrencyId')}`),
    amount: Yup.string().required(`${translate('Validation.Amount')}`),
    recieveDate: Yup.string().required(`${translate('Validation.recieveDate')}`),
  });

  const defaultValues = useMemo<Incomes>(
    () => ({
      incomeId: selectedIncome?.incomeId,
      incomeCategoryId: selectedIncome?.incomeCategoryId || undefined,
      amount: selectedIncome?.amount || undefined,
      carrencyId: selectedIncome?.carrencyId || undefined,
      description: selectedIncome?.description || '',
      recieveDate: selectedIncome?.recieveDate || new Date(),
      attachment: selectedIncome?.attachment || new Date(),
    }),
    [selectedIncome]
  );

  const methods = useForm<Incomes>({
    resolver: yupResolver(NewIncomeSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: Incomes) => {
    const newData = {
      ...data,
      branchId: 1,
      recieveDate: new Date(data.recieveDate!),
    };

    try {
      if (data.incomeId === undefined) {
        // --- create ---
        await createIncome(newData);
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.Income.list);
      } else {
        // --- update ---
        await updateIncome(newData);
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        navigate(PATH_DASHBOARD.Income.list);
      }
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      } catch {
        setError('afterSubmit', { ...err, message: err.message || 'Unexpected error' });
      }
    }
  };


  useEffect(() => {
    loadCurrencyTypeDDL();
    loadIncomeCategoryDDL();
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFSelect name="incomeCategoryId" label={translate('IncomeCategory.IncomeCategory')}>
                <option value="" />
                {IncomeCategoryOption.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.text}
                  </option>
                ))}
              </RHFSelect>
              <RHFSelect name="carrencyId" label={translate('Income.Currency')}>
                {' '}
                <option value="" />
                {CurrencyTypeOption.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.text}
                  </option>
                ))}
              </RHFSelect>
              <RHFTextField name="amount" label={translate('Income.Amount')} showAsterisk={true} />
              <LocalizDatePicker
                name="recieveDate"
                label={translate('Income.Date')}
                control={control}
                showAsterisk={false}
              />
              <RHFTextField
                name="serialNumber"
                label={translate('RentPaymentPrint.SerialNumber')}
                showAsterisk={true}
              />
              <RHFTextField
                name="description"
                label={translate('Income.Description')}
                showAsterisk={true}
              />

              <Stack spacing={2}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    name="attachment"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label="Attach File"
                    name="attachment"
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedIncome();
                  navigate(PATH_DASHBOARD.Income.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
